import { useFormikContext } from "formik";
import { useEffect, useMemo } from "react";

import __ from "web/Layout/Translations";

import HideAnimation from "web/Components/Common/HideAnimation";

import { useGetCompanyDetailsQuery } from "web/features/company/companyApiSlice";

import RentableGroupCalculator from "./RentableGroupCalculator";
import RentableGroupCalculatorInstruction from "./RentableGroupCalculatorInstruction";
import RentableGroupCalculatorResult from "./RentableGroupCalculatorResult";
import RentableGroupCalculatorSelectedGroup from "./RentableGroupCalculatorSelectedGroup";
import RentableGroupCalculatorStatements from "./RentableGroupCalculatorStatements";
import RentableGroupCalculatorTotalIncomeMessage from "./RentableGroupCalculatorTotalIncomeMessage";
import shouldShowCalculator from "./utils/shouldShowCalculator";
import useCalculateRentableGroup from "./utils/useCalculateRentableGroup";
import useRentableGroupCalculatorStatements from "./utils/useRentableGroupsCalculatorStatements";
import getRentableGroupCalculatorCopy from "./utils/getRentableGroupCalculatorCopy";

export interface IRentableGroupCalculatorValues {
  income: {
    me: number;
    people: { role: string; income: number }[];
  };
  "fill-calculator-checkbox"?: boolean;
  "financing-checkbox"?: boolean;
  incomeGroupId?: string;
  agreements?: Record<string, boolean>;
}

const MAX_TOTAL_INCOME = 999999999.99;

export type Statements = ("fill-calculator-checkbox" | "financing-checkbox")[];

const RentableGroupCalculatorContainer = () => {
  const { values, setFieldValue } =
    useFormikContext<IRentableGroupCalculatorValues>();

  const statements = useRentableGroupCalculatorStatements();

  const { peopleCount, averageIncome, incomeGroup, totalIncome } =
    useCalculateRentableGroup(values);

  const { data: companyDetails } = useGetCompanyDetailsQuery();
  const calculatorCopy = useMemo(
    () => getRentableGroupCalculatorCopy(companyDetails?.id || undefined),
    [companyDetails?.id]
  );

  const {
    instructionHeader,
    instruction,
    peopleCount: peopleCountCopy,
    averageIncome: averageIncomeCopy,
    incomeGroup: incomeGroupCopy,
    incomeGroupDeclared: incomeGroupDeclaredCopy,
    noIncomeGroupAvailable,
  } = calculatorCopy;

  useEffect(() => {
    setFieldValue("incomeGroupId", incomeGroup?.id);
  }, [incomeGroup?.id]);

  const showCalculator = useMemo(
    () => shouldShowCalculator(values, statements),
    [values, statements]
  );

  const showSelectedGroup = useMemo(
    () =>
      incomeGroup === null ||
      shouldShowCalculator(values, ["financing-checkbox"]),
    [values, incomeGroup]
  );

  const incomeGroupCopyDisplayed = useMemo(() => {
    const incomeGroupCopyToShow = showCalculator
      ? incomeGroupCopy
      : incomeGroupDeclaredCopy;
    return incomeGroup?.id ? incomeGroupCopyToShow : noIncomeGroupAvailable;
  }, [showCalculator, incomeGroup?.id]);

  const isTotalIncomeTooHigh = useMemo(
    () => !!totalIncome && totalIncome > MAX_TOTAL_INCOME,
    [totalIncome]
  );

  useEffect(() => {
    if (isTotalIncomeTooHigh) {
      setFieldValue("isTotalIncomeTooHigh", true);
    } else {
      setFieldValue("isTotalIncomeTooHigh", false);
    }
  }, [isTotalIncomeTooHigh]);

  return (
    <>
      <HideAnimation isOpen={showCalculator}>
        <RentableGroupCalculatorInstruction
          header={__(instructionHeader)}
          paragraph={__(instruction)}
        />
      </HideAnimation>

      <RentableGroupCalculatorStatements statements={statements} />

      <HideAnimation isOpen={showCalculator}>
        <RentableGroupCalculator />
        <RentableGroupCalculatorResult
          peopleCount={peopleCount}
          peopleCountCopy={__(peopleCountCopy)}
          averageIncome={averageIncome}
          averageIncomeCopy={__(averageIncomeCopy)}
        />
      </HideAnimation>
      {isTotalIncomeTooHigh && (
        <RentableGroupCalculatorTotalIncomeMessage totalIncome={totalIncome!} />
      )}
      <HideAnimation isOpen={showSelectedGroup}>
        <RentableGroupCalculatorSelectedGroup
          incomeGroup={incomeGroup?.frontendName}
          incomeGroupCopy={__(incomeGroupCopyDisplayed)}
        />
      </HideAnimation>
    </>
  );
};
export default RentableGroupCalculatorContainer;
